import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import Cookies from "js-cookie";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import {
  AssignScopeTabs,
  GroupSelectList,
  filterAgentsByCurrentUserGroups,
  groupMatchesQuery,
  loadGroupsForDepartment,
  normalizeGroups,
} from "./GroupAssignParts";

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#ea580c", "#db2777", "#4f46e5", "#0d9488"];
const avatarColorFor = (name) => AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length];

const buildAssigneesFromSelection = (agentIds, agentsList) => {
  const list = Array.isArray(agentsList) ? agentsList : [];
  return (agentIds || []).map((userId) => {
    const agent = list.find((a) => a.userId === userId);
    return {
      assigneeId: userId,
      assigneeName: agent?.name ?? String(userId),
    };
  });
};

export default function ReAssignModal({
  openReassign,
  setOpenReassign,
  selectedtask,
  setIsChecked,
  setTaskData,
  setselectedtask,
  setSelectedTasks,
  setAssigneesToUnassign,
  resetFilters,
  resetFiltersAfterAssign,
}) {
  const context = useContext(AppContext);
  const { setSelectAll, langMode, activityId, bumpTaskListFetchEpoch } =
    context;
  const [fieldAgent, setFieldAgent] = useState();
  const [selectedfieldAgent, setselectedfieldAgent] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // New state variable
  const cancelButtonRef = useRef(null);
  const { theme } = useContext(AppContext);
  const [allFieldAgents, setAllFieldAgents] = useState([]);
  const [displayedAgents, setDisplayedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // ------- group assign -------- start
  const [scope, setScope] = useState("users");
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  // ------- group assign -------- end

  useEffect(() => {}, [fieldAgent]);

  useEffect(() => {
    if (openReassign && !isDataFetched && activityId) {
      const fetchData = (activityId) => {
        const token = Cookies.get("accessToken");

        // resolved params issue by harshit vachhani
        if (!activityId) {
          return;
        }

        axios
          .get(
            `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/user/field-agents-by-workflow?workflowId=${activityId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          )
          .then(async (response) => {
            const rawAgents = response?.data?.body?.value;
            // Only show field agents who belong to a group assigned to this user
            // (blank group list => show all).
            const agents = rawAgents
              ? await filterAgentsByCurrentUserGroups(rawAgents)
              : rawAgents;
            if (agents) {
              setFieldAgent(agents);
              setAllFieldAgents(agents);
              setDisplayedAgents(agents); // Show first 10 records initially
            } else {
              setError("No field agents found");
            }
            setIsDataFetched(true);
          })
          .catch((error) => {
            console.error("An error occurred:", error);
          });
      };
      fetchData(activityId);
      // ------- group assign -------- start
      loadGroupsForDepartment().then(setAvailableGroups);
      // ------- group assign -------- end
    }
    if (!openReassign) {
      setIsDataFetched(false);
      setselectedfieldAgent([]);
      setSearchQuery("");
      // ------- group assign -------- start
      setScope("users");
      setSelectedGroupId(null);
      // ------- group assign -------- end
    }
  }, [openReassign, isDataFetched]);

  const handleFieldAgentSelect = (userId) => {
    setselectedfieldAgent((prevAgents) => {
      let updatedAgents;
      if (prevAgents.includes(userId)) {
        updatedAgents = prevAgents.filter((agentId) => agentId !== userId); // Deselect
      } else {
        updatedAgents = [...prevAgents, userId]; // Select
      }
      console.log("Updated Field Agents: ", updatedAgents); // Log updated list
      return updatedAgents;
    });
  };

  const handleSubmit = async (e) => {
    if (isSubmitting) return;

    if (!selectedtask?.length) {
      toast.warn(translations[langMode].plzselectatleatone);
      return;
    }
    // ------- group assign -------- start
    // Send agents and/or group — whichever is selected (independent of the active tab).
    const hasGroup = selectedGroupId != null;
    const hasAgents = selectedfieldAgent?.length > 0;
    if (!hasGroup && !hasAgents) {
      toast.warn(translations[langMode].plzselectedforilfagrntoasing);
      return;
    }
    // ------- group assign -------- end

    setIsSubmitting(true);
    const temp = JSON.stringify({
      taskIdList: selectedtask,
      // ------- group assign -------- start
      ...(hasAgents ? { fieldAgentIdList: selectedfieldAgent } : {}),
      ...(hasGroup ? { groupId: selectedGroupId } : {}),
      // ------- group assign -------- end
      workflowId: activityId,
    });
    const token = Cookies.get("accessToken");
    const clearSelection = () => {
      setselectedfieldAgent([]);
      if (typeof setselectedtask === "function") {
        setselectedtask([]);
      }
    };
    fetch(
      `${process.env.REACT_APP_BASE_BACKEND_API_URL}api/v1/task/re-assign`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: temp,
      },
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (data?.header?.code != null && data.header.code !== 600) {
          toast.error(
            data?.body?.error || translations[langMode].erroccureduringtask,
          );
          return;
        }
        if (typeof bumpTaskListFetchEpoch === "function") {
          bumpTaskListFetchEpoch();
        }
        setIsChecked(false);
        setSelectAll(false);
        setOpenReassign(false);
        toast.success(translations[langMode].fieldagentassinged);

        const newAssignees = buildAssigneesFromSelection(
          selectedfieldAgent,
          allFieldAgents,
        );
        setTaskData((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((task) => {
            const isSelected = (selectedtask || []).some(
              (sid) => String(sid) === String(task.taskId),
            );
            if (!isSelected) return { ...task, isChecked: false };
            return {
              ...task,
              taskStatus: "Assigned",
              // ------- group assign -------- start
              // Show selected agents optimistically; group members resolve on the
              // server, so keep existing assignees and let the refetch fill them in.
              assignees: hasAgents ? newAssignees : task.assignees,
              // ------- group assign -------- end
              isChecked: false,
            };
          });
        });
        clearSelection();
        if (typeof setSelectedTasks === "function") setSelectedTasks([]);
        if (typeof setAssigneesToUnassign === "function")
          setAssigneesToUnassign([]);
        if (typeof resetFiltersAfterAssign === "function") {
          resetFiltersAfterAssign();
        } else if (typeof resetFilters === "function") {
          resetFilters();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        setTimeout(() => {
          setIsSubmitting(false);
        }, 1000);
      });
  };

  const handleSearch = (e) => {
    // Keep the raw text for display so the user can type upper/lower case;
    // match case-insensitively.
    const value = e.target.value;
    setSearchQuery(value);
    const query = value.toLowerCase();
    const filteredAgents = allFieldAgents.filter((agent) =>
      agent.name.toLowerCase().includes(query),
    );
    setDisplayedAgents(filteredAgents);
  };

  const displayedAgentIds = useMemo(
    () => (displayedAgents || []).map((agent) => agent.userId),
    [displayedAgents]
  );

  const allDisplayedSelected =
    displayedAgentIds.length > 0 &&
    displayedAgentIds.every((id) => selectedfieldAgent.includes(id));

  const handleToggleSelectAllDisplayed = () => {
    if (!displayedAgentIds.length) return;
    setselectedfieldAgent((prevAgents) => {
      if (allDisplayedSelected) {
        return prevAgents.filter((id) => !displayedAgentIds.includes(id));
      }
      return Array.from(new Set([...prevAgents, ...displayedAgentIds]));
    });
  };

  // ------- group assign -------- start
  const groups = useMemo(() => normalizeGroups(availableGroups), [availableGroups]);
  const filteredGroups = useMemo(() => {
    if (!(searchQuery || "").trim()) return groups;
    return groups.filter((g) => groupMatchesQuery(g, searchQuery));
  }, [groups, searchQuery]);
  // Members allowed for the group tab = field agents with access to this workflow.
  const workflowAgentIds = useMemo(
    () => (allFieldAgents || []).map((agent) => agent?.userId).filter((id) => id != null),
    [allFieldAgents]
  );
  const isGroupScope = scope === "groups";
  // Agents and group are independent selections; either or both can be submitted.
  const canConfirm = selectedfieldAgent.length > 0 || selectedGroupId != null;
  // ------- group assign -------- end

  return (
   <Transition.Root show={openReassign} as={Fragment}>
  <Dialog
    as="div"
    className="relative z-[10000]"
    initialFocus={cancelButtonRef}
    onClose={setOpenReassign}
  >
    {/* Overlay */}
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
    </Transition.Child>

    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-2 sm:p-6">
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 scale-95 translate-y-4"
        enterTo="opacity-100 scale-100 translate-y-0"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 scale-100 translate-y-0"
        leaveTo="opacity-0 scale-95 translate-y-4"
      >
        <Dialog.Panel
          className={`relative w-full max-w-3xl transform overflow-hidden rounded-2xl border shadow-2xl transition-all
            ${theme === "dark"
              ? "bg-gray-800 text-white border-gray-700"
              : theme === "high-contrast"
              ? "bg-black text-yellow-300 border border-yellow-300"
              : "bg-white text-slate-800 border-slate-100"
            }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-5 sm:py-4">
            <h3 className="text-base sm:text-lg font-semibold text-slate-800">
              {translations[langMode].FieldStaffList}
            </h3>
            <ModalCloseButton
              onClick={() => setOpenReassign(false)}
              ariaLabel="Close modal"
            />
          </div>

          <p className="px-4 sm:px-5 pt-3 sm:pt-4 mb-2 sm:mb-3 text-xs sm:text-sm text-slate-500">
            {isGroupScope
              ? (translations[langMode]?.selectgrouptoassign ?? "Select group(s) to assign.")
              : (translations[langMode]?.selectassigntounassign ?? "Select agent(s) to assign.")}
          </p>

          {/* Body */}
          <div className="px-4 sm:px-5 pb-4 sm:pb-5">
            <AssignScopeTabs scope={scope} onScopeChange={setScope} langMode={langMode} />
            <div className="mb-3 flex items-center gap-2">
              <input
                type="text"
                placeholder={
                  isGroupScope
                    ? (translations[langMode]?.searchGroup ?? "Search Groups")
                    : translations[langMode].searchfieldAgent
                }
                value={searchQuery}
                onChange={handleSearch}
                className="w-full min-w-0 flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-400"
              />
              {!isGroupScope && (
                <CommonButton colorIntent="primary"
                  type="button"
                  variant="outline"
                  onClick={handleToggleSelectAllDisplayed}
                  disabled={displayedAgentIds.length === 0}
                  tooltipText={
                    allDisplayedSelected
                      ? (translations[langMode].deselectAllAgentsTooltip ?? "Deselect all visible agents")
                      : (translations[langMode].selectAllAgentsTooltip ?? "Select all visible agents")
                  }
                  tooltipPosition="top"
                  className="!h-10 !min-h-[40px] !px-3 !text-xs !whitespace-nowrap"
                >
                  {allDisplayedSelected
                    ? (translations[langMode].deselectAll ?? "Deselect All")
                    : (translations[langMode].selectAll ?? "Select All")}
                </CommonButton>
              )}
            </div>
            {/* Error */}
            {/* {error && !isGroupScope && <div className="text-red-500 text-sm">{error}</div>} */}

            {/* Agent / Group list */}
            <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-2 mb-6">
              {isGroupScope ? (
                <GroupSelectList
                  groups={filteredGroups}
                  selectedGroupId={selectedGroupId}
                  onSelectGroup={setSelectedGroupId}
                  query={searchQuery}
                  langMode={langMode}
                  allowedMemberIds={workflowAgentIds}
                />
              ) : displayedAgents && displayedAgents.length > 0 ? (
                displayedAgents.map((agent) => (
                  <label
                    key={agent.userId}
                    className={`flex items-center gap-3 cursor-pointer py-2 px-2.5 rounded-lg transition
                    ${theme === "dark"
                      ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                      : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      onChange={() => handleFieldAgentSelect(agent.userId)}
                      checked={selectedfieldAgent.includes(agent.userId)}
                      className="h-4 w-4 rounded border-slate-300 accent-[#236DB4] text-blue-600"
                    />
                    <span
                      className="h-8 w-8 rounded-full text-white flex items-center justify-center text-sm font-semibold shrink-0"
                      style={{ background: avatarColorFor(agent?.name) }}
                    >
                      {(agent?.name || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-slate-700">{agent.name}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-slate-400 py-4 text-center">
                  {translations[langMode]?.nofieldagent ?? "No agents available"}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <CommonButton colorIntent="danger" onClick={() => setOpenReassign(false)} variant="danger" tooltipPosition="top">
                {translations[langMode]?.cancel ?? "Cancel"}
              </CommonButton>
              <CommonButton colorIntent="warning"
                onClick={handleSubmit}
                variant="primary"
                disabled={isSubmitting || !canConfirm}
                tooltipText={
                  isSubmitting
                    ? translations[langMode].reassigningprocess
                    : !canConfirm
                      ? translations[langMode].plzselectedforilfagrntoasing
                      : translations[langMode].reassingselectedtasktochosen
                }
                tooltipPosition="top"
              >
                {isSubmitting ? "Reassigning..." : (translations[langMode].reassign ?? "Reassign")}
              </CommonButton>
            </div>
          </div>
        </Dialog.Panel>
      </Transition.Child>
    </div>
  </Dialog>
</Transition.Root>

  );
}
